import i18n from '@/utils/i18n';
import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { formatDate } from '@/utils/date';
import { isPastDate } from '@/utils/order-helpers';
import { Text } from 'react-native';
import { TYPOGRAPHY } from '@/styles';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';

interface LastOrderedMessageDateProps {
  variantPackUUID: string;
}

export const LastOrderedMessageDate = React.memo(
  ({ variantPackUUID }: LastOrderedMessageDateProps) => {
    const { lastPurchaseInfoMap } = useVariantPackInfoContext();
    const [styles] = useStyles();

    const lastPurchaseInfo = lastPurchaseInfoMap[variantPackUUID];
    if (!lastPurchaseInfo) {
      return null;
    }

    // fulfillmentDate and orderDate already include invoice dates as fallbacks
    // when orders don't exist (handled by getVariantPackLastPurchaseInfo in the backend)
    const metricDate =
      lastPurchaseInfo.fulfillmentDate ?? lastPurchaseInfo.orderDate;

    if (!metricDate) {
      return null;
    }

    const noPurchaseHistory =
      lastPurchaseInfo.quantity === null || lastPurchaseInfo.quantity === 0;
    const fulfillmentDateIsInPast = isPastDate(metricDate);
    const formattedDate = formatDate(metricDate, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });

    const labelWithoutQuantity = fulfillmentDateIsInPast
      ? i18n.t('delivered_on_date', {
          date: formattedDate,
        })
      : i18n.t('arriving_on_date', {
          date: formattedDate,
        });
    const labelWithQuantity = fulfillmentDateIsInPast
      ? i18n.t('delivered_quantity_on_date', {
          date: formattedDate,
          quantity: lastPurchaseInfo.quantity,
        })
      : i18n.t('arriving_quantity_on_date', {
          date: formattedDate,
          quantity: lastPurchaseInfo.quantity,
        });

    // Only render the date if quantity is null or 0, which can happen for
    // order history from order invoice line items
    return (
      <Text style={[TYPOGRAPHY.xsmall, styles.textColor]}>
        {noPurchaseHistory ? labelWithoutQuantity : labelWithQuantity}
      </Text>
    );
  },
);

const useStyles = createStyles((theme) => ({
  textColor: {
    color: theme.sk.color.text.tertiary,
  },
}));
